import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  ShipperClaim,
  ShipperClaimListResponse,
  ShipperClaimFilter,
  ShipperClaimEmail,
  ShipperClaimDetailResponse,
  ShipperClaimUpdateSummaryWithGeminiDto,
  AdminOwner
} from '../../../data/interfaces';

@Injectable({
  providedIn: 'root'
})
export class ShipperClaimService {

  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  getShipperClaimList(filter: ShipperClaimFilter): Observable<ShipperClaimListResponse> {
    return this.http.get<ShipperClaimListResponse>(`${this.apiUrl}/shipper-claim/list`, { params: filter as any });
  }

  getShipperClaimDetail(shipperClaimId: string): Observable<ShipperClaim> {
    return this.http.get<ShipperClaimDetailResponse>(`${this.apiUrl}/shipper-claim/details/${shipperClaimId}`)
      .pipe(
        map(response => response.data)
      );
  }

  createShipperClaim(shipperClaim: Partial<ShipperClaim>): Observable<ShipperClaim> {
    return this.http.post<any>(`${this.apiUrl}/shipper-claim/create`, shipperClaim)
      .pipe(
        map(response => response.data)
      );
  }

  updateShipperClaim(shipperClaimId: string, updates: Partial<ShipperClaim>): Observable<ShipperClaim> {
    return this.http.put<ShipperClaimDetailResponse>(`${this.apiUrl}/shipper-claim/update/${shipperClaimId}`, updates)
      .pipe(
        map(response => response.data)
      );
  }

  addEmailToClaim(shipperClaimId: string, email: Partial<ShipperClaimEmail> & { files?: File[]; carrierOnly?: boolean }): Observable<ShipperClaim> {
    // Check if files are attached
    if (email.files && email.files.length > 0) {
      // Use FormData for file upload
      const formData = new FormData();
      
      // Add email data
      formData.append('emailMessage', email.emailMessage || '');
      if (email.subject) formData.append('subject', email.subject);
      if (email.from) formData.append('from', email.from);
      formData.append('sender', email.sender || 'Admin');
      
      // Add recipients
      if (email.to) {
        email.to.forEach(recipient => {
          formData.append('to[]', recipient);
        });
      }
      
      if (email.cc && email.cc.length > 0) {
        email.cc.forEach(recipient => {
          formData.append('cc[]', recipient);
        });
      }
      
      formData.append('showEmail', String(email.showEmail ?? true));
      
      // Add carrierOnly flag
      if (email.carrierOnly !== undefined) {
        formData.append('carrierOnly', String(email.carrierOnly));
      }
      
      // Add files
      email.files.forEach(file => {
        formData.append('attachments', file, file.name);
      });
      
      return this.http.post<ShipperClaimDetailResponse>(`${this.apiUrl}/shipper-claim/${shipperClaimId}/email/add`, formData)
        .pipe(
          map(response => response.data)
        );
    } else {
      // No files, send as JSON (existing behavior)
      const { files, ...emailPayload } = email; // Remove files property
      return this.http.post<ShipperClaimDetailResponse>(`${this.apiUrl}/shipper-claim/${shipperClaimId}/email/add`, emailPayload)
        .pipe(
          map(response => response.data)
        );
    }
  }

  updateEmail(shipperClaimId: string, emailId: string, email: Partial<ShipperClaimEmail>): Observable<ShipperClaim> {
    return this.http.put<ShipperClaimDetailResponse>(`${this.apiUrl}/shipper-claim/${shipperClaimId}/email/${emailId}/update`, email)
      .pipe(
        map(response => response.data)
      );
  }

  deleteShipperClaim(shipperClaimId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/shipper-claim/delete/${shipperClaimId}`);
  }

  // Additional methods based on your controller
  deleteEmailFromClaim(shipperClaimId: string, emailId: string): Observable<ShipperClaim> {
    return this.http.delete<ShipperClaimDetailResponse>(`${this.apiUrl}/shipper-claim/${shipperClaimId}/email/${emailId}/delete`)
      .pipe(
        map(response => response.data)
      );
  }

  markEmailAsRead(shipperClaimId: string, emailId: string): Observable<ShipperClaim> {
    return this.http.patch<ShipperClaimDetailResponse>(`${this.apiUrl}/shipper-claim/${shipperClaimId}/email/${emailId}/mark-read`, {})
      .pipe(
        map(response => response.data)
      );
  }

  toggleEmailVisibility(shipperClaimId: string, emailId: string, showEmail: boolean): Observable<ShipperClaim> {
    return this.http.patch<ShipperClaimDetailResponse>(`${this.apiUrl}/shipper-claim/${shipperClaimId}/email/${emailId}/toggle-visibility`, { showEmail })
      .pipe(
        map(response => response.data)
      );
  }

  getShipperClaimStats(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/shipper-claim/stats`);
  }

  getClaimsByShipment(shipmentId: string): Observable<ShipperClaimListResponse> {
    return this.http.get<ShipperClaimListResponse>(`${this.apiUrl}/shipper-claim/shipment/${shipmentId}`);
  }

  getClaimsByShipper(shipperId: string): Observable<ShipperClaimListResponse> {
    return this.http.get<ShipperClaimListResponse>(`${this.apiUrl}/shipper-claim/shipper/${shipperId}`);
  }

  getClaimsByCarrier(carrierId: string): Observable<ShipperClaimListResponse> {
    return this.http.get<ShipperClaimListResponse>(`${this.apiUrl}/shipper-claim/carrier/${carrierId}`);
  }

  updateSummaryWithGemini(shipperClaimId: string, geminiDto: ShipperClaimUpdateSummaryWithGeminiDto): Observable<ShipperClaim> {
    return this.http.post<ShipperClaimDetailResponse>(`${this.apiUrl}/shipper-claim/${shipperClaimId}/update-summary-with-gemini`, geminiDto)
      .pipe(
        map(response => response.data)
      );
  }

  getShipperShipments(shipperId: string, query: any): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/shipper-claim/shipper/${shipperId}/shipments`, { params: query });
  }

  /** Search for a shipment by number (for add-claim modal). Returns shipment with shipper and carrier. */
  searchShipmentByNumber(shipmentNo: string): Observable<{ shipment: any }> {
    return this.http.get<{ success: boolean; data: { shipment: any } }>(
      `${this.apiUrl}/shipper-claim/shipment-search`,
      { params: { shipmentNo: shipmentNo?.trim() || '' } }
    ).pipe(
      map(res => res.data)
    );
  }

  /** Record whether a document passes the Cargors check. Flagging needs a reason: the shipper is shown it. */
  reviewClaimDocument(
    shipperClaimId: string,
    documentType: string,
    s3Key: string,
    status: 'Accepted' | 'Flagged',
    flagReason?: string,
    reviewedBy?: string
  ): Observable<ShipperClaimDetailResponse> {
    return this.http.post<ShipperClaimDetailResponse>(
      `${this.apiUrl}/shipper-claim/${shipperClaimId}/document/review`,
      { documentType, s3Key, status, flagReason, reviewedBy }
    );
  }

  markAllEmailsAdminRead(shipperClaimId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/shipper-claim/${shipperClaimId}/mark-all-admin-read`, {});
  }

  getAvailableOwners(): Observable<AdminOwner[]> {
    return this.http.get<{ data: AdminOwner[] }>(`${this.apiUrl}/shipper-claim/available-owners`)
      .pipe(
        map(response => response.data)
      );
  }

  uploadClaimDocument(claimId: string, claimNo: string, documentType: string, file: File): Observable<ShipperClaim> {
    const formData = new FormData();
    formData.append('claimNo', claimNo);
    formData.append('documentType', documentType);
    formData.append('files', file);
    return this.http.post<ShipperClaimDetailResponse>(
      `${this.apiUrl}/shipper-claim/${claimId}/document/upload`,
      formData
    ).pipe(
      map(response => response.data)
    );
  }

  deleteClaimDocument(claimId: string, documentType: string, s3Key: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.apiUrl}/shipper-claim/${claimId}/document/delete`,
      { documentType, s3Key }
    );
  }

  downloadEmailAttachmentsZip(claimId: string, emailId: string): Observable<Blob> {
    return this.http.get(
      `${this.apiUrl}/shipper-claim/${claimId}/email/${emailId}/attachments/download`,
      { responseType: 'blob' }
    );
  }

}
